// src/components/ErrorFallback.js
import React from 'react';
import { Alert, Box, Button, Typography, Paper, Divider } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ErrorFallback - Used by the error boundary to display error information
 * and provide recovery options
 */
const ErrorFallback = ({
  error,
  resetErrorBoundary,
  showDetails = false,
  additionalActions = null
}) => {
  // Determine if it looks like a network/API error
  const isNetworkError = error &&
    (error.message === 'Failed to fetch' ||
     error.message.includes('Network error'));

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        m: 2,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        maxWidth: '800px',
        mx: 'auto'
      }}
    >
      <Typography variant="h5" color="error" gutterBottom>
        {isNetworkError ? 'Connection Error' : 'Application Error'}
      </Typography>

      <Alert severity="error" sx={{ mb: 2 }}>
        {isNetworkError
          ? 'Unable to connect to the server. Please check your internet connection or try again later.'
          : error?.message || 'An unexpected error occurred'
        }
      </Alert>

      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={resetErrorBoundary}
        >
          Try Again
        </Button>

        {additionalActions}
      </Box>

      {showDetails && error?.stack && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" gutterBottom>
            Error Details (for developers):
          </Typography>
          <Typography
            variant="caption"
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: '200px',
              whiteSpace: 'pre-wrap'
            }}
          >
            {error.stack}
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default ErrorFallback;